import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeType = 'light' | 'dark' | 'blue' | 'green' | 'purple' | 'gold' | 'pink' | 'orange' | 'blueaccent' | 'cyan';

interface Theme {
  name: string;
  colors: {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    success: string;
    danger: string;
    warning: string;
    info: string;
    sidebar: string;
    sidebarText: string;
    header: string;
    card: string;
    shadow: string;
  };
}

const themes: Record<ThemeType, Theme> = {
  light: {
    name: 'Light',
    colors: {
      primary: '#2563eb',
      secondary: '#64748b',
      background: '#f8fafc',
      surface: '#ffffff',
      text: '#0f172a',
      textSecondary: '#64748b',
      border: '#e2e8f0',
      success: '#10b981',
      danger: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
      sidebar: '#ffffff',
      sidebarText: '#0f172a',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(0,0,0,0.08)'
    }
  },
  dark: {
    name: 'Dark',
    colors: {
      primary: '#3b82f6',
      secondary: '#64748b',
      background: '#0f172a',
      surface: '#1e293b',
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      border: '#334155',
      success: '#10b981',
      danger: '#ef4444',
      warning: '#f59e0b',
      info: '#06b6d4',
      sidebar: '#1e293b',
      sidebarText: '#f8fafc',
      header: '#1e293b',
      card: '#334155',
      shadow: 'rgba(0,0,0,0.25)'
    }
  },
  blue: {
    name: 'Blue',
    colors: {
      primary: '#1e40af',
      secondary: '#3b82f6',
      background: '#f0f9ff',
      surface: '#ffffff',
      text: '#1e3a8a',
      textSecondary: '#3730a3',
      border: '#bfdbfe',
      success: '#059669',
      danger: '#dc2626',
      warning: '#d97706',
      info: '#0284c7',
      sidebar: '#eff6ff',
      sidebarText: '#1e3a8a',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(30,64,175,0.08)'
    }
  },
  green: {
    name: 'Green',
    colors: {
      primary: '#16a34a',
      secondary: '#22c55e',
      background: '#f0fdf4',
      surface: '#ffffff',
      text: '#14532d',
      textSecondary: '#166534',
      border: '#bbf7d0',
      success: '#16a34a',
      danger: '#dc2626',
      warning: '#ca8a04',
      info: '#0891b2',
      sidebar: '#ecfdf5',
      sidebarText: '#14532d',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(22,163,74,0.08)'
    }
  },
  purple: {
    name: 'Purple',
    colors: {
      primary: '#7c3aed',
      secondary: '#a855f7',
      background: '#faf5ff',
      surface: '#ffffff',
      text: '#581c87',
      textSecondary: '#6b21a8',
      border: '#e9d5ff',
      success: '#059669',
      danger: '#dc2626',
      warning: '#d97706',
      info: '#0284c7',
      sidebar: '#f3e8ff',
      sidebarText: '#581c87',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(124,58,237,0.08)'
    }
  },
  gold: {
    name: 'Gold',
    colors: {
      primary: '#d97706',
      secondary: '#f59e0b',
      background: '#fffbeb',
      surface: '#ffffff',
      text: '#92400e',
      textSecondary: '#b45309',
      border: '#fed7aa',
      success: '#059669',
      danger: '#dc2626',
      warning: '#d97706',
      info: '#0284c7',
      sidebar: '#fef3c7',
      sidebarText: '#92400e',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(217,119,6,0.08)'
    }
  },
  pink: {
    name: 'Pink',
    colors: {
      primary: '#e11d48',
      secondary: '#f43f5e',
      background: '#fff1f2',
      surface: '#ffffff',
      text: '#881337',
      textSecondary: '#9f1239',
      border: '#fecdd3',
      success: '#059669',
      danger: '#dc2626',
      warning: '#d97706',
      info: '#0284c7',
      sidebar: '#ffe4e6',
      sidebarText: '#881337',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(225,29,72,0.08)'
    }
  },
  orange: {
    name: 'Orange',
    colors: {
      primary: '#ea580c',
      secondary: '#fb923c',
      background: '#fff7ed',
      surface: '#ffffff',
      text: '#9a3412',
      textSecondary: '#c2410c',
      border: '#fed7aa',
      success: '#059669',
      danger: '#dc2626',
      warning: '#d97706',
      info: '#0284c7',
      sidebar: '#ffedd5',
      sidebarText: '#9a3412',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(234,88,12,0.08)'
    }
  },
  blueaccent: {
    name: 'Blue',
    colors: {
      primary: '#0ea5e9',
      secondary: '#38bdf8',
      background: '#f0f9ff',
      surface: '#ffffff',
      text: '#0c4a6e',
      textSecondary: '#0369a1',
      border: '#bae6fd',
      success: '#059669',
      danger: '#dc2626',
      warning: '#d97706',
      info: '#0284c7',
      sidebar: '#e0f2fe',
      sidebarText: '#0c4a6e',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(14,165,233,0.08)'
    }
  },
  cyan: {
    name: 'Cyan',
    colors: {
      primary: '#0891b2',
      secondary: '#06b6d4',
      background: '#ecfeff',
      surface: '#ffffff',
      text: '#164e63',
      textSecondary: '#0e7490',
      border: '#a5f3fc',
      success: '#059669',
      danger: '#dc2626',
      warning: '#d97706',
      info: '#0284c7',
      sidebar: '#cffafe',
      sidebarText: '#164e63',
      header: '#ffffff',
      card: '#ffffff',
      shadow: 'rgba(8,145,178,0.08)'
    }
  }
};

interface ThemeContextType {
  theme: ThemeType;
  themeConfig: Theme;
  setTheme: (theme: ThemeType) => void;
  availableThemes: { key: ThemeType; name: string }[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeType>(() => {
    const saved = localStorage.getItem('autoTrade_theme');
    return (saved as ThemeType) || 'light';
  });

  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
    localStorage.setItem('autoTrade_theme', newTheme);
  };

  const availableThemes = Object.keys(themes).map(key => ({
    key: key as ThemeType,
    name: themes[key as ThemeType].name
  }));

  useEffect(() => {
    const root = document.documentElement;
    const themeColors = themes[theme].colors;

    Object.entries(themeColors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-${key}`, value);
    });
  }, [theme]);

  return (
    <ThemeContext.Provider value={{
      theme,
      themeConfig: themes[theme],
      setTheme,
      availableThemes
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};